import React, { useContext, useMemo } from "react";
import { AxiosError } from "axios";
import { ApiError } from "fe-shared/src/types/common";
import { QueryKey, useInfiniteQuery } from "react-query";

import { useHistory, useRouteMatch } from "react-router-dom";

import UserContext from "contexts/userContext";
import TransfersDataGrid from "fe-shared-transactions/src/components/TransfersDataGrid";

import { getRequestParams } from "fe-shared-transactions/src/helpers/transfersConverter";

import { hasPermission } from "fe-shared/src/helpers/permissions";
import { PermissionLevel } from "fe-shared/src/types/domain";
import { TRANSFERS_QUERY_KEY, PAGE_SIZE, PERMISSIONS } from "consts";
import {
  TransferResponse,
  TransferListItem,
} from "fe-shared-transactions/src/types";
import {
  DataGridFilters,
  DataGridOptions,
} from "fe-shared/src/components/DataGrid/modules/types";
import useStorage from "fe-shared/src/hooks/useStorage";
import useQueryFilters from "fe-shared-transactions/src/hooks/useQueryFilters";
import { getTransfersFilterParams } from "fe-shared-transactions/src/helpers/queryString";

import * as api from "fe-shared-transactions/src/components/TransfersDataGrid/modules/api";

interface Props {
  lastOpenedTransfersNumber: string | null;
  clearLastOpenedTransfersNumber: () => void;
}

const Transfers = ({
  lastOpenedTransfersNumber,
  clearLastOpenedTransfersNumber,
}: Props) => {
  const history = useHistory();
  const match = useRouteMatch();
  const user = useContext(UserContext);
  const [options, setOptions] = useStorage<DataGridOptions>(
    "portalTransfersOptions",
    {
      sortDirection: "desc",
      sortField: "completedDate",
    },
    true
  );
  const [filters, setFilters] = useStorage<DataGridFilters>(
    "portalTransfersFilters",
    {},
    true
  );

  const canViewTransfers = hasPermission(user?.permissions, {
    name: PERMISSIONS.transfersList,
    level: PermissionLevel.Read,
  });
  const canViewDetails = hasPermission(user?.permissions, {
    name: PERMISSIONS.transfersDetails,
    level: PermissionLevel.Read,
  });

  const canExport = hasPermission(user?.permissions, {
    name: PERMISSIONS.transfersListExport,
    level: PermissionLevel.Full,
  });

  const requestParams = useMemo(() => getRequestParams(filters, options), [
    filters,
    options,
  ]);

  const transfersQueryKey: QueryKey = [
    TRANSFERS_QUERY_KEY,
    requestParams,
    undefined,
  ];

  const transfers = useInfiniteQuery<
    TransferResponse<TransferListItem>,
    AxiosError<ApiError>
  >(transfersQueryKey, api.getTransfers, {
    enabled: canViewTransfers,
    refetchOnWindowFocus: false,
    keepPreviousData: true,
    getFetchMore: (lastGroup, allGroups) =>
      allGroups.length * PAGE_SIZE < lastGroup.total
        ? allGroups.length + 1
        : undefined,
  });

  useQueryFilters(setFilters, getTransfersFilterParams);

  return canViewTransfers ? (
    <TransfersDataGrid
      isInternal
      filters={filters}
      options={options}
      canExport={canExport}
      setFilters={setFilters}
      setOptions={setOptions}
      transfers={transfers}
      transfersQueryKey={transfersQueryKey}
      lastOpenedTransfersNumber={lastOpenedTransfersNumber}
      clearLastOpenedTransfersNumber={clearLastOpenedTransfersNumber}
      onCellClicked={(e) => {
        const eventTag = "A";
        const currentNode = (e.event?.target as Element).nodeName;
        const isEventTagClicked = eventTag === currentNode;

        if (canViewDetails && !isEventTagClicked) {
          history.push(`${match.url}/${e.data.transferNumber}`);
        }
      }}
    />
  ) : null;
};

export default Transfers;
